import { format } from "date-fns";
import { groupBy } from "lodash";
import { getLiveData, GetLiveDataResponse } from "../../api/liveDataApi";
import {
  getOccupancy,
  GetOccupancyDataResponse,
} from "../../api/occupancyDataApi";
import { LiveMetrics, RokaChartData } from "../../types/rokaTypes";
import { useFetchWithToken } from "../utility-hoks/useFetchWithToken";

export const useDataApi = () => {
  const { fetchWithToken } = useFetchWithToken();

  const fetchChartData = async (
    locationName: string,
    startTime: Date,
    period: "day" | "hour" = "hour",
    sensorFilter: string | null,
  ): Promise<RokaChartData> => {
    const response = await fetchWithToken(
      getOccupancy(locationName, startTime, period, sensorFilter),
    );
    const result = (await response.json()) as GetOccupancyDataResponse;
    const { sensors } = result.body;

    const filteredEntries = sensors
      .filter(
        (sensor) =>
          sensorFilter === "" ||
          sensorFilter === null ||
          sensor.id === sensorFilter,
      )
      .flatMap((sensor) => sensor.entries);
    const entriesByTimestamp = groupBy(
      filteredEntries,
      (entry) => entry.timestamp,
    );

    const totalOccupancyByTimestamp = Object.keys(entriesByTimestamp).map(
      (key) => ({
        timestamp: key,
        occupancy: entriesByTimestamp[key]
          .sort(function (a, b) {
            return Date.parse(b.timestamp) - Date.parse(a.timestamp);
          })
          .reduce((sum, entry) => sum + entry.occupancy, 0),
      }),
    );

    return {
      sensors: sensors.map((sensor) => sensor.id),
      labels: totalOccupancyByTimestamp.map((entry) =>
        format(new Date(entry.timestamp), "HH:mm"),
      ),
      data: totalOccupancyByTimestamp.map((entry) => entry.occupancy),
    };
  };

  const fetchLiveMetrics = async (
    locationName: string,
  ): Promise<LiveMetrics> => {
    const response = await fetchWithToken(getLiveData(locationName));
    const result = await response.json();

    // Pretty weird, but we can actually get a 404 from lambda
    if (result.statusCode === 404) {
      // This should only happen when there have been no data entries for the event.
      // We just want to display something some sort of "initial" state here.
      return {
        currentOccupancy: 0,
        maxOccupancyToday: 0,
        occupancyDiffLastHour: 0,
        lastUpdated: undefined,
      };
    }

    // Parse data
    const parsedResponse = result as GetLiveDataResponse;
    const body = parsedResponse.body;

    return {
      ...body,
      lastUpdated: body.lastUpdated ? new Date(body.lastUpdated) : undefined,
    };
  };

  return {
    fetchChartData,
    fetchLiveMetrics,
  };
};
